export const parseCurl = (curl) => {
  try {
    if (!curl || typeof curl !== "string") {
      return { url: "", method: "GET", headers: {}, body: "" };
    }

    // cleanup invisible characters / multiline
    curl = curl
      .replace(/\u00A0/g, " ")
      .replace(/\u200B/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/\\\r?\n/g, " ")
      .trim();

    // Extract URL
    const urlMatch = curl.match(/(https?:\/\/[^\s'"]+)/i);
    let url = urlMatch ? urlMatch[1] : "";

    // Remove trailing quotes or %27 safely
    if (url) {
      url = url.replace(/(['%27]+)$/g, "");
      try {
        url = decodeURIComponent(url);
      } catch {
        // ignore
      }
    }

    // Extract method from -X
    let method = "GET";
    const methodMatch = curl.match(/-X\s+(\w+)/i);
    if (methodMatch) method = methodMatch[1].toUpperCase();

    // Force GET if -G flag exists
    if (/\s-G\s/.test(curl)) method = "GET";

    // Default to POST if data exists and no explicit method
    const hasDataFlag =
      /\s(--data|-d|--data-raw|--data-binary|--form|-F|--data-urlencode)\s+/.test(
        curl
      );
    if (!methodMatch && !/\s-G\s/.test(curl) && hasDataFlag) method = "POST";

    // Extract headers (-H, --header)
    const headers = {};
    const headerMatches = [
      ...curl.matchAll(/(?:-H|--header)\s+['"]?([^'"\n]+)['"]?/gi),
    ];
    headerMatches.forEach((m) => {
      const headerLine = m[1].trim();
      const [key, ...rest] = headerLine.split(":");
      if (key && rest.length) headers[key.trim()] = rest.join(":").trim();
    });

    // Extract body
    const dataFlags = ["-d", "--data", "--data-raw", "--data-binary", "--form", "-F"];
    let body = "";

    for (const flag of dataFlags) {
      const matchQuoted = curl.match(new RegExp(`${flag}\\s+(['"])([\\s\\S]*?)\\1`));
      if (matchQuoted) {
        body = matchQuoted[2].trim();
        break;
      }
      const matchUnquoted = curl.match(new RegExp(`${flag}\\s+([^\\s]+)`));
      if (matchUnquoted) {
        body = matchUnquoted[1].trim();
        break;
      }
    }

    // Handle --data-urlencode (multiple occurrences)
    const params = [];
    const urlEncodeRegex = /--data-urlencode\s+(?:'([^']+)'|"([^"]+)"|([^\s]+))/gi;
    let encoded;
    while ((encoded = urlEncodeRegex.exec(curl)) !== null) {
      const param = encoded[1] || encoded[2] || encoded[3];
      params.push(param.trim());
    }

    // Append encoded params to URL if GET
    if (method === "GET" && params.length) {
      const query = params.join("&");
      url += (url.includes("?") ? "&" : "?") + query;
    }

    // Parse JSON body to preserve formatting
    if (body && method !== "GET") {
      try {
        const parsedBody = JSON.parse(body);
        body = JSON.stringify(parsedBody);
      } catch {
        // keep as-is
      }
    }

    return { url, method, headers, body };
  } catch (error) {
    console.error("Error parsing curl:", error);
    return { url: "", method: "GET", headers: {}, body: "" };
  }
};

export function parseNestedStrings(obj) {
  if (typeof obj === "string") {
    const trimmed = obj.trim();
    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
      return obj;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return parseNestedStrings(parsed);
    } catch {
      return obj;
    }
  }

  if (Array.isArray(obj)) {
    return obj.map(parseNestedStrings);
  }

  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, parseNestedStrings(v)])
    );
  }

  return obj;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Basic JSON syntax highlighting that matches WebhooksSms ApiBuilder.css classes
export const syntaxHighlight = (json) => {
  if (typeof json !== "string") {
    json = JSON.stringify(json, null, 2);
  }
  json = json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|\b-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?\b)/g,
      (match) => {
        let cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
          } else {
            cls = "string";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
        } else if (/null/.test(match)) {
          cls = "null";
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  return json;
};

export function safeJsonParse(input) {
  if (!input) return { ok: true, value: {} };
  try {
    return { ok: true, value: JSON.parse(input) };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export function convertKeyValueArrayToObject(arr) {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, item) => {
    const key = item?.key?.trim();
    if (!key) return acc;
    acc[key] = item?.value ?? "";
    return acc;
  }, {});
}


